// Citation generator - build MLA 9 / APA 7 references for the common source
// types students actually cite (website, book, journal article), copy one, or
// keep a running Works Cited / References list (saved locally) and copy it all.
import { el, btn, input, wrap, sel, field, note, copy as copyText, announce, saveFlag } from "./ui.js";
import { load, mutateCitations, onChange } from "./cite-store.js";
import { mla, apa, format, toHtml, toPlain, pickFields } from "./cite-format.js";

// Field sets per source type. Each is [key, label, placeholder].
const FIELDS = {
	website: [
		["author", "Author (Last, First)", "Doe, Jane"],
		["title", "Page title", "How Photosynthesis Works"],
		["site", "Website / publisher", "National Geographic"],
		["year", "Year", "2023"],
		["month", "Month Day (optional)", "March 4"],
		["url", "URL", "https://example.com/article"],
		["accessed", "Date accessed (MLA)", "10 May 2024"],
	],
	book: [
		["author", "Author (Last, First)", "Orwell, George"],
		["title", "Book title", "Nineteen Eighty-Four"],
		["publisher", "Publisher", "Secker & Warburg"],
		["year", "Year", "1949"],
	],
	journal: [
		["author", "Author (Last, First)", "Smith, John"],
		["title", "Article title", "Cell Division in Plants"],
		["journal", "Journal name", "Journal of Botany"],
		["volume", "Volume", "12"],
		["issue", "Issue", "3"],
		["year", "Year", "2020"],
		["pages", "Pages", "45-59"],
	],
};

export function cite(root) {
	const s = wrap(root); s.classList.add("rv-cite");
	// What the list shows. Changes go through mutateCitations, which edits what is stored now.
	let saved = load();
	const change = (fn) => {
		const ok = mutateCitations(fn);
		saved = load(); renderSaved();
		flag.set(ok);
		if (!ok) announce("Not saved. Your device is out of space for saves.");
		return ok;
	};
	const stop = onChange(() => { if (!root.isConnected) { stop(); return; } saved = load(); renderSaved(); });
	const esc = (t) => { const d = document.createElement("div"); d.textContent = t == null ? "" : String(t); return d.innerHTML; };

	const bar = el("div", "rv-trow");
	const styleSel = sel([["mla", "MLA 9"], ["apa", "APA 7"]], "mla");
	const typeSel = sel([["website", "Website"], ["book", "Book"], ["journal", "Journal article"]], "website");
	bar.append(field("Style", styleSel), field("Source type", typeSel));
	s.appendChild(bar);

	const formWrap = el("div", "rv-cite-fields");
	s.appendChild(formWrap);

	const outWrap = el("div", "rv-cite-out");
	const outLabel = el("div", "rv-field-label", "Preview");
	const out = el("div", "rv-cite-preview");
	out.setAttribute("aria-live", "polite");
	const outActions = el("div", "rv-trow");
	const flag = saveFlag();
	const copyBtn = btn("Copy", () => { copyText(toPlain(current())); announce("Citation copied"); }, "rv-tbtn");
	const addBtn = btn("Add to list", () => { const c = current(); if (!toPlain(c).trim()) return; const style = styleSel.value; change((list) => { let id = Date.now(); while (list.some((x) => x.id === id)) id++; list.push({ id, style, text: c }); }); }, "rv-tbtn rv-tbtn-accent");
	outActions.append(copyBtn, addBtn);
	outWrap.append(outLabel, out, outActions, flag);
	s.appendChild(outWrap);

	const savedWrap = el("div", "rv-cite-saved");
	s.appendChild(savedWrap);

	let inputs = {};
	function buildFields() {
		formWrap.textContent = ""; inputs = {};
		FIELDS[typeSel.value].forEach(([k, label, ph]) => {
			const i = input("text", { placeholder: ph });
			i.addEventListener("input", update);
			inputs[k] = i;
			formWrap.appendChild(field(label, i));
		});
		update();
	}
	function values() { const f = {}; Object.keys(inputs).forEach((k) => (f[k] = inputs[k].value)); return f; }
	function current() { const gen = styleSel.value === "apa" ? apa : mla; return gen(typeSel.value, values()); }
	function update() {
		const c = current();
		out.innerHTML = toPlain(c).trim() ? toHtml(c, esc) : '<span class="rv-tnote">Fill in the fields above to build a citation.</span>';
	}
	function renderSaved() {
		savedWrap.textContent = "";
		if (!saved.length) return;
		const head = el("div", "rv-cite-saved-head");
		head.append(el("span", "rv-tool-sub", saved.length === 1 ? "Works Cited (1)" : "Works Cited (" + saved.length + ")"),
			btn("Copy all", () => copyText(saved.map((c) => toPlain(c.text)).join("\n")), "rv-tbtn"),
			btn("Clear", () => {
				if (!confirm("Clear the saved list?")) return;
				// Only what is shown: a citation added in another tab since stays.
				const shown = new Set(saved.map((x) => x.id));
				change((list) => list.filter((x) => !shown.has(x.id)));
			}, "rv-tbtn rv-tbtn-danger"));
		savedWrap.appendChild(head);
		saved.forEach((c) => {
			const rowEl = el("div", "rv-cite-saved-item");
			const txt = el("div", "rv-cite-saved-text"); txt.innerHTML = toHtml(c.text, esc);
			const rm = btn("✕", () => change((list) => list.filter((x) => x.id !== c.id)), "rv-fc-card-del");
			rm.setAttribute("aria-label", "Remove");
			rowEl.append(el("span", "rv-cite-saved-badge", String(c.style || "").toUpperCase()), txt);
			// Saved with its fields ("Cite this page"): it can switch between MLA and APA.
			if (c.f && typeof c.f === "object" && c.type === "website") {
				const other = c.style === "apa" ? "mla" : "apa";
				const sw = btn(other === "apa" ? "APA" : "MLA", () => change((list) => {
					const x = list.find((y) => y.id === c.id);
					if (!x) return;
					x.style = other;
					x.text = format(other, "website", pickFields(x.f));
				}), "rv-tbtn");
				sw.setAttribute("aria-label", "Show in " + (other === "apa" ? "APA 7" : "MLA 9"));
				rowEl.append(sw);
			}
			rowEl.append(rm);
			savedWrap.appendChild(rowEl);
		});
	}

	styleSel.addEventListener("change", update);
	typeSel.addEventListener("change", buildFields);
	buildFields();
	renderSaved();
	s.appendChild(note("Citations are formatted for the common cases and stored only in this browser. Always double-check against your assignment's style guide."));
}
